import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  Config,
  Params,
  useCreate,
  useDelete,
  useHttp,
  usePatch
} from "@/hooks/useHttp"

import type { GetUseMutationOptions } from "@/hooks/useHttp"
import type {
  AgentPlatformConnections,
  AgentPlatformConnectRequest,
  AgentPlatformKey,
  AgentPlatformTypes,
  DeploymentKeysResponse,
  DeploymentsResponse,
  DeploymentVersionResponse,
  FiltersDeploymentResponse,
  SensorsCountResponse
} from "./useDeployments.types"

export const useGetDeploymentVersion = <
  T = DeploymentVersionResponse,
  S = T
>() => {
  const context = useHttp<T, S>(`${RESOURCES_NAMES.DEPLOYMENTS}/version`)

  return context
}

export const useGetDeploymentKey = <T = DeploymentKeysResponse, S = T>() => {
  const context = useCreate<T, S>(
    `${RESOURCES_NAMES.AUTHENTICATION.DEPLOYMENT_KEYS}`
  )

  return context
}

export const useGetFiltersDeployments = <T = FiltersDeploymentResponse, S = T>(
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.DEPLOYMENTS}/filters`,
    {},
    config
  )

  return context
}

export const useGetDeployments = <T = DeploymentsResponse, S = T>(
  params: Params<object> = {},
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(RESOURCES_NAMES.DEPLOYMENTS, params, config)

  return context
}

export const useGetSensorsCount = <T = SensorsCountResponse, S = T>(
  params: Params<{
    fromDate: number
    toDate: number
    granularity?: string
    environment?: string
  }>,
  config?: Config<T, S>
) => {
  const context = useHttp<T, S>(
    `${RESOURCES_NAMES.DEPLOYMENTS}/statistics`,
    params,
    config
  )

  return context
}

const { PLATFORM, PRE_INSTALL, CONNECTIONS } = RESOURCES_NAMES.AGENT_PLATFORMS

export const useGetAgentPlatformConnections = <P extends AgentPlatformKey>(
  platform: P,
  enabled = true
) => {
  type Connection = AgentPlatformTypes[P]["connection"]

  // An API without the route fails; the page shows that state itself, so no
  // failure toasts or throws
  const context = useHttp<AgentPlatformConnections<Connection>>(
    CONNECTIONS(platform),
    undefined,
    {
      enabled,
      silentErrors: true,
      throwOnError: false
    }
  )

  return context
}

// A connection change also changes the platform's rows on the deployments list
const getConnectionMutationOptions =
  <T, S>(platform: AgentPlatformKey): GetUseMutationOptions<T, S> =>
  (queryClient) => ({
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [CONNECTIONS(platform)]
        }),
        queryClient.invalidateQueries({
          queryKey: [RESOURCES_NAMES.DEPLOYMENTS]
        })
      ])
  })

export const usePreInstallAgentPlatform = <P extends AgentPlatformKey>(
  platform: P
) =>
  useCreate<
    AgentPlatformTypes[P]["preInstall"],
    AgentPlatformTypes[P]["settings"]
  >(PRE_INSTALL(platform))

// Connects an account, or updates one already connected
export const useConnectAgentPlatform = <P extends AgentPlatformKey>(
  platform: P
) =>
  useCreate<unknown, AgentPlatformConnectRequest<P>>(
    PLATFORM(platform),
    undefined,
    undefined,
    getConnectionMutationOptions(platform)
  )

// The body is the connection's settings themselves, at its needle
export const useUpdateAgentPlatform = <P extends AgentPlatformKey>(
  platform: P
) =>
  usePatch<
    unknown,
    { needle: string; data: AgentPlatformTypes[P]["settings"] }
  >(
    CONNECTIONS(platform),
    undefined,
    undefined,
    getConnectionMutationOptions(platform)
  )

// Disconnects the connection whose id is the needle
export const useDisconnectAgentPlatform = (platform: AgentPlatformKey) =>
  useDelete<unknown, string>(
    CONNECTIONS(platform),
    undefined,
    undefined,
    getConnectionMutationOptions(platform)
  )
